import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { UploadCloud, Sparkles, X, FileText, AlertTriangle, CheckCircle2, HelpCircle, Loader2 } from "lucide-react";

const TIPOS_DOC = ["Oficio", "Resolutivo", "Plano", "Escritura", "Constancia", "Pago de derechos", "Evidencia", "Otro"];
const CAMPOS = [
  ["autoridad", "Autoridad emisora"],
  ["folio", "Folio / Expediente"],
  ["fecha_ingreso", "Ingreso a autoridad", "date"],
  ["fecha_resolucion", "Fecha de resolución", "date"],
  ["fecha_vencimiento", "Vencimiento de vigencia", "date"],
  ["costo", "Costo (derechos, MXN)", "number"],
];

function ChipConfianza({ c }) {
  const nivel = c >= 0.85 ? ["bg-emerald-100 text-emerald-700", "alta"] : c >= 0.6 ? ["bg-amber-100 text-amber-700", "revisar"] : ["bg-red-100 text-red-600", "baja"];
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${nivel[0]}`}>{Math.round(c * 100)}% {nivel[1]}</span>;
}

// ---------- Drawer de revisión ----------
export function RevisionDrawer({ analysis, tramite, tramiteOverride, onClose, onAplicado }) {
  const res = analysis?.resultado;
  const [fase, setFase] = useState("veredicto"); // veredicto | campos
  const [vals, setVals] = useState({});
  const [checks, setChecks] = useState({});
  const [reemplazar, setReemplazar] = useState({});
  const [reqsSel, setReqsSel] = useState({});
  const [estatus, setEstatus] = useState("none");
  const [tipoDoc, setTipoDoc] = useState("Otro");
  const [nombreDoc, setNombreDoc] = useState("");
  const [comentario, setComentario] = useState("");
  const [aplicando, setAplicando] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!analysis || !res) return;
    setFase(res.veredicto === "PERTENECE" ? "campos" : "veredicto");
    const v = {}, ch = {};
    CAMPOS.forEach(([k]) => {
      const c = res.campos?.[k] || {};
      v[k] = c.value ?? "";
      ch[k] = c.value != null && (c.confidence || 0) >= 0.6;
    });
    setVals(v); setChecks(ch); setReemplazar({});
    const rq = {};
    (res.requisitos_cumplidos || []).forEach((r) => { rq[r] = true; });
    setReqsSel(rq);
    setEstatus(res.estatus_sugerido || "none");
    setTipoDoc(TIPOS_DOC.includes(res.document_type) ? res.document_type : "Otro");
    setNombreDoc(res.suggested_name || analysis.nombre_original);
    setComentario("");
    // visor
    api.get(`/ai/analyses/${analysis.id}/archivo`, { responseType: "blob" })
      .then((r) => setPdfUrl(URL.createObjectURL(new Blob([r.data], { type: analysis.content_type }))))
      .catch(() => {});
    return () => pdfUrl && URL.revokeObjectURL(pdfUrl);
  }, [analysis?.id]); // eslint-disable-line

  if (!analysis || !res) return null;

  const feedback = async (pertenece) => {
    try {
      await api.post(`/ai/analyses/${analysis.id}/feedback`, { pertenece, comentario: "" });
      if (pertenece) setFase("campos");
      else setFase("no_pertenece");
    } catch (e) { toast.error(apiError(e)); }
  };

  const descartar = async () => {
    try {
      await api.delete(`/ai/analyses/${analysis.id}`);
      toast.success("Archivo descartado");
      onAplicado(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const aplicar = async (soloDocumento = false) => {
    if (soloDocumento && !comentario.trim()) return toast.error("Escribe un comentario para guardarlo de todos modos");
    setAplicando(true);
    try {
      const campos = {};
      if (!soloDocumento) CAMPOS.forEach(([k]) => { if (checks[k] && vals[k] !== "") campos[k] = vals[k]; });
      await api.post(`/ai/analyses/${analysis.id}/aplicar`, {
        tramite_id: tramiteOverride || null,
        campos,
        reemplazar: Object.keys(reemplazar).filter((k) => reemplazar[k]),
        requisitos: soloDocumento ? [] : Object.keys(reqsSel).filter((k) => reqsSel[k]),
        estatus: soloDocumento || estatus === "none" ? null : estatus,
        condicionantes: soloDocumento ? [] : (res.condicionantes || []),
        tipo_documento: soloDocumento ? "Otro" : tipoDoc,
        nombre_documento: nombreDoc,
        comentario,
      });
      toast.success("Captura asistida aplicada y registrada en bitácora");
      onAplicado(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    setAplicando(false);
  };

  const dudas = res.dudas || [];
  const esImagen = analysis.content_type?.startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="revision-drawer">
      <div className="flex-1 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="w-full max-w-[560px] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="px-5 py-4 border-b flex items-center gap-3 bg-[#002B49] text-white">
          <Sparkles size={17} className="text-[#EAD8C0]" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold">Revisión de captura asistida</p>
            <p className="text-[11px] text-white/60 truncate">{analysis.nombre_original}</p>
          </div>
          <button data-testid="drawer-cerrar" onClick={onClose} className="text-white/60 hover:text-white p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Veredicto */}
          {res.veredicto === "NO_PERTENECE" || fase === "no_pertenece" ? (
            <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="banner-no-pertenece">
              <p className="flex items-center gap-2 text-red-700 font-bold text-sm"><AlertTriangle size={15} /> Este archivo no parece pertenecer a este trámite</p>
              <p className="text-xs text-red-600 mt-1">{res.razon}</p>
              <div className="mt-3 space-y-2">
                <Button data-testid="btn-descartar" onClick={descartar} variant="destructive" size="sm" className="w-full">Descartar archivo</Button>
                <Textarea placeholder="Comentario obligatorio para guardarlo de todos modos..." rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} className="text-xs" />
                <Button data-testid="btn-guardar-igual" onClick={() => aplicar(true)} disabled={aplicando} variant="outline" size="sm" className="w-full">Guardar de todos modos como Otro</Button>
              </div>
            </div>
          ) : fase === "veredicto" && res.veredicto === "DUDA" ? (
            <div className="m-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="banner-duda">
              <p className="flex items-center gap-2 text-amber-800 font-bold text-sm"><HelpCircle size={15} /> ¿Este documento pertenece a este trámite?</p>
              <p className="text-xs text-amber-700 mt-1">{res.razon}</p>
              <ul className="mt-2 space-y-1.5">
                {dudas.map((d, i) => (
                  <li key={i} className="text-xs text-amber-800 bg-white/70 rounded p-2">
                    <span className="font-semibold">{d.duda}</span>
                    {d.evidencia && <span className="block text-amber-600 italic mt-0.5">“{d.evidencia}”</span>}
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex gap-2">
                <Button data-testid="btn-si-pertenece" onClick={() => feedback(true)} size="sm" className="flex-1 bg-[#002B49] hover:bg-[#00395f]">Sí, pertenece</Button>
                <Button data-testid="btn-no-pertenece" onClick={() => feedback(false)} size="sm" variant="outline" className="flex-1">No pertenece</Button>
              </div>
            </div>
          ) : (
            <div className="m-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-2" data-testid="banner-pertenece">
              <CheckCircle2 size={15} className="text-emerald-600" />
              <p className="text-xs text-emerald-800"><span className="font-bold">Documento validado</span> · {res.razon}</p>
            </div>
          )}

          {/* Visor */}
          {pdfUrl && (
            <div className="mx-4 mb-3 border rounded-lg overflow-hidden bg-slate-100" data-testid="visor-documento">
              {esImagen ? <img src={pdfUrl} alt="Documento" className="w-full max-h-56 object-contain" />
                : <iframe src={pdfUrl} title="Documento" className="w-full h-56" />}
            </div>
          )}

          {/* Campos */}
          {fase === "campos" && res.veredicto !== "NO_PERTENECE" && (
            <div className="px-4 pb-4 space-y-4">
              <div className="space-y-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Campos detectados (revisa y aplica)</p>
                {CAMPOS.map(([k, label, tipo]) => {
                  const c = res.campos?.[k] || {};
                  const actual = tramite?.[k];
                  const tieneActual = actual !== null && actual !== undefined && actual !== "" && actual !== 0;
                  return (
                    <div key={k} className={`rounded-lg border p-3 ${checks[k] ? "border-[#00A3B5]/40 bg-[#00A3B5]/5" : "border-slate-200"}`} data-testid={`campo-${k}`}>
                      <div className="flex items-center gap-2">
                        <Checkbox data-testid={`check-${k}`} checked={!!checks[k]} onCheckedChange={(v) => setChecks({ ...checks, [k]: !!v })} />
                        <span className="text-xs font-semibold text-[#002B49] flex-1">{label}</span>
                        {c.value != null && <ChipConfianza c={c.confidence || 0} />}
                      </div>
                      <Input data-testid={`input-${k}`} type={tipo || "text"} step="any" value={vals[k] ?? ""}
                        onChange={(e) => setVals({ ...vals, [k]: e.target.value })} className="mt-2 h-8 text-sm" />
                      {c.evidence?.quote && (
                        <p className="text-[11px] text-slate-400 italic mt-1.5">“{c.evidence.quote}” · pág. {c.evidence.page}</p>
                      )}
                      {tieneActual && (
                        <label className="flex items-center gap-2 mt-2 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5 cursor-pointer">
                          <Checkbox data-testid={`reemplazar-${k}`} checked={!!reemplazar[k]} onCheckedChange={(v) => setReemplazar({ ...reemplazar, [k]: !!v })} />
                          Reemplazar · Actual: <b>{String(actual)}</b> → Propuesto: <b>{String(vals[k])}</b>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              {(res.requisitos_cumplidos || []).length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Requisitos que este documento acredita</p>
                  {res.requisitos_cumplidos.map((r) => (
                    <label key={r} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
                      <Checkbox checked={!!reqsSel[r]} onCheckedChange={(v) => setReqsSel({ ...reqsSel, [r]: !!v })} /> {r}
                    </label>
                  ))}
                </div>
              )}

              {(res.condicionantes || []).length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Condicionantes (se agregan a Notas)</p>
                  {res.condicionantes.map((cn, i) => <p key={i} className="text-xs text-slate-600">• {cn}</p>)}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Estatus sugerido</p>
                  <Select value={estatus} onValueChange={setEstatus}>
                    <SelectTrigger data-testid="select-estatus-sugerido" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin cambio —</SelectItem>
                      {["Aprobado", "Rechazado", "En prevención/subsanación", "Ingresado a autoridad"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Tipo de documento</p>
                  <Select value={tipoDoc} onValueChange={setTipoDoc}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS_DOC.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Nombre del documento</p>
                <Input data-testid="input-nombre-doc" value={nombreDoc} onChange={(e) => setNombreDoc(e.target.value)} className="h-8 text-sm" />
              </div>
              {(res.warnings || []).map((w, i) => <p key={i} className="text-[11px] text-amber-600">⚠ {w}</p>)}
            </div>
          )}
        </div>

        {fase === "campos" && res.veredicto !== "NO_PERTENECE" && (
          <div className="border-t p-4 flex gap-2 bg-slate-50">
            <Button data-testid="btn-aplicar" onClick={() => aplicar(false)} disabled={aplicando} className="flex-1 bg-[#002B49] hover:bg-[#00395f]">
              {aplicando ? "Aplicando..." : "Aplicar seleccionados"}
            </Button>
            <Button data-testid="btn-descartar-campos" onClick={descartar} variant="outline">Descartar</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Zona de carga con IA (por trámite) ----------
export function ZonaCapturaIA({ tramite, onChanged }) {
  const [pendientes, setPendientes] = useState([]); // [{id, nombre, estado, veredicto?, error?}]
  const [revision, setRevision] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const timers = useRef({});

  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const poll = (aid) => {
    timers.current[aid] = setInterval(async () => {
      try {
        const r = await api.get(`/ai/analyses/${aid}`);
        if (r.data.estado !== "analizando") {
          clearInterval(timers.current[aid]);
          setPendientes((p) => p.map((x) => (x.id === aid ? { ...x, ...r.data } : x)));
        }
      } catch (e) { clearInterval(timers.current[aid]); }
    }, 2500);
  };

  const subir = async (files) => {
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post(`/tramites/${tramite.id}/documentos/analizar`, fd);
        setPendientes((p) => [...p, { id: r.data.analysis_id, nombre_original: f.name, estado: "analizando" }]);
        poll(r.data.analysis_id);
      } catch (e) { toast.error(`${f.name}: ${apiError(e)}`); }
    }
  };

  const abrirRevision = async (aid) => {
    const r = await api.get(`/ai/analyses/${aid}`);
    setRevision(r.data);
  };

  const quitar = (aid) => setPendientes((p) => p.filter((x) => x.id !== aid));

  return (
    <div>
      <div data-testid="dropzone-ia"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); subir(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${drag ? "border-[#00A3B5] bg-[#00A3B5]/5" : "border-slate-300 hover:border-[#00A3B5]/60"}`}>
        <input ref={inputRef} data-testid="dropzone-input" type="file" multiple accept=".pdf,image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => { subir(e.target.files); e.target.value = ""; }} />
        <UploadCloud size={22} className="mx-auto text-[#00A3B5] mb-1" />
        <p className="text-sm font-semibold text-[#002B49]">Arrastra documentos aquí (PDF, JPG, PNG)</p>
        <p className="text-[11px] text-slate-400 mt-0.5 flex items-center justify-center gap-1"><Sparkles size={11} className="text-[#00A3B5]" /> La IA los clasifica, extrae folio, fechas, vigencia y costo, y tú revisas antes de aplicar</p>
      </div>

      {pendientes.length > 0 && (
        <div className="mt-3 space-y-2">
          {pendientes.map((a) => (
            <div key={a.id} data-testid={`analisis-card-${a.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <FileText size={15} className="text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#002B49] truncate">{a.nombre_original}</p>
                {a.estado === "analizando" && <p className="text-[11px] text-[#00A3B5] flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Leyendo documento... clasificando... extrayendo campos...</p>}
                {a.estado === "error" && <p className="text-[11px] text-red-500">{a.error || "No fue posible analizar; captura manual disponible"}</p>}
                {a.estado === "listo" && (
                  <p className={`text-[11px] font-bold ${a.veredicto === "PERTENECE" ? "text-emerald-600" : a.veredicto === "DUDA" ? "text-amber-600" : "text-red-500"}`}>
                    {a.veredicto === "PERTENECE" ? "✓ Pertenece · campos listos" : a.veredicto === "DUDA" ? "? Requiere tu confirmación" : "✗ No parece pertenecer"}
                  </p>
                )}
              </div>
              {a.estado === "listo" && !a.aplicado && (
                <Button data-testid={`btn-revisar-${a.id}`} size="sm" onClick={() => abrirRevision(a.id)} className="h-7 text-xs bg-[#00A3B5] hover:bg-[#008a99]">Revisar</Button>
              )}
              {a.estado !== "analizando" && (
                <button onClick={() => quitar(a.id)} className="text-slate-300 hover:text-slate-500"><X size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {revision && (
        <RevisionDrawer analysis={revision} tramite={tramite}
          onClose={() => setRevision(null)}
          onAplicado={() => { quitar(revision.id); onChanged(); }} />
      )}
    </div>
  );
}

// ---------- Atajo global: subir varios documentos sin elegir trámite ----------
export function LoteIA({ pid, onChanged }) {
  const [filas, setFilas] = useState([]);
  const [revision, setRevision] = useState(null); // {analysis, tramite, override}
  const inputRef = useRef(null);
  const timers = useRef({});

  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const poll = (aid) => {
    timers.current[aid] = setInterval(async () => {
      try {
        const r = await api.get(`/ai/analyses/${aid}`);
        if (r.data.estado !== "analizando") {
          clearInterval(timers.current[aid]);
          setFilas((p) => p.map((x) => (x.id === aid ? { ...x, ...r.data } : x)));
        }
      } catch (e) { clearInterval(timers.current[aid]); }
    }, 2500);
  };

  const subir = async (files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const r = await api.post(`/properties/${pid}/documentos/analizar-lote`, fd);
      r.data.analisis.forEach((a) => {
        setFilas((p) => [...p, { id: a.analysis_id, nombre_original: a.nombre, estado: "analizando" }]);
        poll(a.analysis_id);
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const abrir = async (fila) => {
    const tidSug = fila.resultado?.tramite_sugerido_id;
    if (!tidSug) return toast.error("La IA no identificó el trámite; súbelo desde el detalle del trámite correspondiente");
    try {
      const [ra, rt] = await Promise.all([api.get(`/ai/analyses/${fila.id}`), api.get(`/tramites/${tidSug}`)]);
      setRevision({ analysis: ra.data, tramite: rt.data, override: tidSug });
    } catch (e) { toast.error(apiError(e)); }
  };

  const descartar = async (fila) => {
    try { await api.delete(`/ai/analyses/${fila.id}`); setFilas((p) => p.filter((x) => x.id !== fila.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <input ref={inputRef} type="file" multiple accept=".pdf,image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => { subir(e.target.files); e.target.value = ""; }} />
      <Button data-testid="lote-ia-button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} className="gap-2">
        <Sparkles size={13} className="text-[#00A3B5]" /> Subir documentos (IA)
      </Button>

      {filas.length > 0 && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="lote-tabla">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b bg-slate-50">
                <th className="px-3 py-2">Archivo</th><th className="px-3 py-2">Trámite sugerido</th>
                <th className="px-3 py-2">Confianza</th><th className="px-3 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id} className="border-b border-slate-50">
                  <td className="px-3 py-2 font-semibold text-[#002B49]">{f.nombre_original}</td>
                  <td className="px-3 py-2">
                    {f.estado === "analizando" ? <span className="text-[#00A3B5] flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Analizando...</span>
                      : f.estado === "error" ? <span className="text-red-500">No fue posible analizar</span>
                      : f.tramite_sugerido_nombre || <span className="text-slate-400">No identificado</span>}
                  </td>
                  <td className="px-3 py-2">
                    {f.estado === "listo" && f.resultado ? <ChipConfianza c={f.resultado.confianza_pertenencia || 0} /> : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {f.estado === "listo" && (
                      <span className="flex gap-1 justify-end">
                        <Button size="sm" className="h-6 text-[11px] bg-[#00A3B5] hover:bg-[#008a99]" onClick={() => abrir(f)}>Revisar</Button>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-400" onClick={() => descartar(f)}>Descartar</Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revision && (
        <RevisionDrawer analysis={revision.analysis} tramite={revision.tramite} tramiteOverride={revision.override}
          onClose={() => setRevision(null)}
          onAplicado={() => { setFilas((p) => p.filter((x) => x.id !== revision.analysis.id)); onChanged && onChanged(); }} />
      )}
    </>
  );
}
